import { CATALOG, getDefinition } from "../catalog";
import { effectiveDimensions, effectiveTerminals, isStudTerminal } from "../effective";
import { migrate } from "../schema/migrate";
import { SCHEMA_VERSION } from "../schema/version";
import { impliedZoneDistances } from "../zone-lengths";
import { parseFuseMarker } from "./fuse-marker";
import { planGeneratedZones, type GeneratedZoneKind } from "./system-zones";
import type {
  Cable,
  Category,
  ComponentDefinition,
  ComponentNode,
  System,
  Terminal,
  TerminalSide,
} from "../types";
// Import the schema directly to keep this shared planner edge-safe.
import type {
  AiSystemComponentDraft,
  AiSystemConnectionDraft,
  AiSystemDraft,
  AiSystemPolarity,
  AiSystemPort,
} from "../../../functions/_ai/system-schema";

/** Deterministic by construction — no `nanoid`, no `Date.now()`, or the edge cache breaks. */

export type SystemDraftError =
  | { kind: "unknown-catalog-id"; refs: string[]; catalogIds: string[] }
  | { kind: "ambiguous-engine-port"; ref: string }
  | {
      kind: "no-terminal";
      ref: string;
      polarity: AiSystemPolarity;
      /** `"missing"` — no terminal on this rail. `"exhausted"` — all claimed, none shareable. */
      reason: "missing" | "exhausted";
      /** Eligible terminals, not free ones. */
      available: number;
    }
  | { kind: "invalid-system"; message: string };

export type SystemDraftResult =
  | {
      ok: true;
      system: System;
      /**
       * Index-aligned with the connection list `draftToSystem` was handed. Connections
       * `relieveFuseBlocks` appends land past its length and are ignored by every chip.
       */
      cableIdsByConnection: string[][];
    }
  | { ok: false; error: SystemDraftError };

/** Left-to-right bands, in current-flow order. `columnOfNode` decides what goes in each. */
const COL_SOURCE = 0;
const COL_TAP = 1; // the switch or shunt that bolts onto a battery
const COL_CONVERT = 2;
const COL_POS_BUS = 3;
const COL_BRANCH = 4;
const COL_LOAD = 5;
const COL_RETURN = 6;

/** How upstream a category is: decides which cable claims a busbar's input post. */
export const SOURCE_RANK: Record<Category, number> = {
  battery: 0,
  engine: 1,
  source: 1,
  solar: 1,
  charger: 2,
  distribution: 3,
  monitor: 4,
  load: 5,
};

/** ABYC E-11.15.4.1.11: no more than four terminals on any one stud. */
const MAX_TERMINALS_PER_STUD = 4;

/**
 * False for distribution gear: the catalog models a bus bar's common as one terminal,
 * but the product is a bar with its own row of studs.
 */
function stackLimited(def: ComponentDefinition): boolean {
  return def.category !== "distribution";
}

/** Top-to-bottom order in a column; `ROW_START_BATTERY` comes from the graph, not the id. */
const ROW_ORDER: Record<Category, number> = {
  source: 0,
  battery: 1,
  engine: 3,
  charger: 0,
  monitor: 1,
  // Inert: solar is a band, not a column, and these two are ordered elsewhere.
  solar: 0,
  distribution: 0,
  load: 0,
};
const ROW_START_BATTERY = 2;

const COLUMN_GAP_PX: Record<number, number> = {
  [COL_SOURCE]: 110,
  [COL_TAP]: 180,
  [COL_CONVERT]: 140,
  [COL_POS_BUS]: 150,
  [COL_BRANCH]: 220,
  [COL_LOAD]: 180,
  [COL_RETURN]: 0,
};
const ROW_GAP_PX = 48;
const SOLAR_BAND_GAP_PX = 140;
/** Keeps the panel band a rectangle the loads don't sit inside; a zone is a rectangle. */
const LOAD_BAND_GAP_PX = 150;

export function draftToSystem(input: AiSystemDraft, context: Pick<System, "designProfileId"> = {}): SystemDraftResult {
  // Order matters: `count` is clamped on distribution parts before relief reads it.
  const { draft } = relieveFuseBlocks(collapseDistributionCounts(input).draft);

  const defs = new Map<string, ComponentDefinition>();
  const unknownRefs: string[] = [];
  const unknownIds: string[] = [];
  for (const c of draft.components) {
    const def = getDefinition(c.catalogId);
    if (!def) {
      unknownRefs.push(c.ref);
      if (!unknownIds.includes(c.catalogId)) unknownIds.push(c.catalogId);
      continue;
    }
    defs.set(c.ref, def);
  }
  if (unknownIds.length) {
    return {
      ok: false,
      error: { kind: "unknown-catalog-id", refs: unknownRefs, catalogIds: unknownIds },
    };
  }

  const nodeIdsByRef = new Map<string, string[]>();
  const nodes: ComponentNode[] = [];
  const usedIds = new Set<string>();
  for (const c of draft.components) {
    const def = defs.get(c.ref)!;
    const base = slug(c.ref) || "part";
    const ids: string[] = [];
    for (let i = 0; i < c.count; i++) {
      const id = uniqueId(c.count === 1 ? base : `${base}-${i + 1}`, usedIds);
      ids.push(id);
      nodes.push(buildDraftNode(id, def, c, i));
    }
    nodeIdsByRef.set(c.ref, ids);
  }

  // Counts on both ends pair index-wise: two batteries onto two chargers is two
  // cables, not four; one counted end shares the single unit on the other.
  type PendingCable = {
    fromNodeId: string;
    toNodeId: string;
    fromRef: string;
    toRef: string;
    fromPort: AiSystemPort;
    toPort: AiSystemPort;
    polarity: AiSystemPolarity;
    lengthM: number;
    lengthEstimated: boolean;
    marker: string | null;
    seriesLink: boolean;
    connectionIndex: number;
  };
  const pending: PendingCable[] = [];
  draft.connections.forEach((conn, connectionIndex) => {
    const fromIds = nodeIdsByRef.get(conn.from) ?? [];
    const toIds = nodeIdsByRef.get(conn.to) ?? [];
    if (!fromIds.length || !toIds.length) return;
    const fanOut = Math.max(fromIds.length, toIds.length);
    for (let i = 0; i < fanOut; i++) {
      pending.push({
        connectionIndex,
        fromNodeId: fromIds[i % fromIds.length],
        toNodeId: toIds[i % toIds.length],
        fromRef: conn.from,
        toRef: conn.to,
        fromPort: conn.fromPort,
        toPort: conn.toPort,
        polarity: conn.polarity,
        lengthM: conn.lengthM,
        lengthEstimated: conn.lengthEstimated,
        marker: conn.marker,
        seriesLink: seriesLinkHonoured(conn.seriesLink, defs.get(conn.from), defs.get(conn.to)),
      });
    }
  });

  // `effectiveTerminals` synthesizes studs only once `studCount` is set, so this
  // must precede allocation.
  const degree = new Map<string, number>();
  for (const p of pending) {
    degree.set(p.fromNodeId, (degree.get(p.fromNodeId) ?? 0) + 1);
    degree.set(p.toNodeId, (degree.get(p.toNodeId) ?? 0) + 1);
  }
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const defByNodeId = new Map<string, ComponentDefinition>();
  for (const c of draft.components) {
    for (const id of nodeIdsByRef.get(c.ref) ?? []) defByNodeId.set(id, defs.get(c.ref)!);
  }
  for (const node of nodes) {
    const def = defByNodeId.get(node.id)!;
    if (def.renderVariant !== "busbar") continue;
    // A return bar is spun 180° by `layoutGeneratedComponents`, so its input post faces nothing: every
    // cable gets a stud and the post is deliberately left empty.
    const landing = degree.get(node.id) ?? 1;
    node.studCount = Math.max(1, isReturnBar(def) ? landing : landing - 1);
  }

  const categoryOfNode = (id: string): Category => defByNodeId.get(id)!.category;
  const incident = new Map<string, number[]>();
  pending.forEach((p, i) => {
    push(incident, p.fromNodeId, i);
    push(incident, p.toNodeId, i);
  });
  const used = new Map<string, Map<string, number>>();
  const resolved = new Map<string, string>(); // `${cableIndex}:${end}` → terminalId

  for (const [nodeId, cableIndexes] of incident) {
    const node = nodeById.get(nodeId)!;
    const def = defByNodeId.get(nodeId)!;
    const terminals = effectiveTerminals(def, node);
    const stacked = new Map<string, number>();
    used.set(nodeId, stacked);

    const ordered = [...cableIndexes].sort((a, b) => {
      const rankA = SOURCE_RANK[categoryOfNode(otherEnd(pending[a], nodeId))];
      const rankB = SOURCE_RANK[categoryOfNode(otherEnd(pending[b], nodeId))];
      return rankA - rankB || a - b;
    });

    for (const index of ordered) {
      const p = pending[index];
      const isFrom = p.fromNodeId === nodeId;
      // The parser rejects `from === to`, so each cable touches this node once.
      const end = isFrom ? "from" : "to";
      const port = isFrom ? p.fromPort : p.toPort;
      const otherDef = defByNodeId.get(otherEnd(p, nodeId))!;
      // A series jumper's ends sit on opposite rails: it leaves the upstream cell's
      // negative post and lands on the next cell's positive one.
      const endPolarity: AiSystemPolarity = p.seriesLink
        ? isFrom
          ? "negative"
          : "positive"
        : p.polarity;
      if (ambiguousEnginePort(def, terminals, endPolarity, port)) {
        return { ok: false, error: { kind: "ambiguous-engine-port", ref: isFrom ? p.fromRef : p.toRef } };
      }
      // A jumper has no side: both cells are the same kind, so the rail names the terminal.
      const role: TerminalRole | null = p.seriesLink
        ? null
        : terminalRole(port, isFrom, def, otherDef);
      const pick = pickTerminal(def, terminals, endPolarity, role, stacked);
      if (!pick.ok) {
        return {
          ok: false,
          error: {
            kind: "no-terminal",
            ref: isFrom ? p.fromRef : p.toRef,
            polarity: endPolarity,
            reason: pick.reason,
            available: pick.available,
          },
        };
      }
      stacked.set(pick.terminal.id, (stacked.get(pick.terminal.id) ?? 0) + 1);
      resolved.set(`${index}:${end}`, pick.terminal.id);
    }
  }

  // The marker names a fuse *family*, never a size: one we recognise becomes
  // `fuseTypeOverride` and feeds the AIC walk, one we don't stays free text.
  const cables: Cable[] = pending.map((p, i) => {
    const cable: Cable = {
      id: `c${i + 1}`,
      fromNodeId: p.fromNodeId,
      fromTerminalId: resolved.get(`${i}:from`)!,
      toNodeId: p.toNodeId,
      toTerminalId: resolved.get(`${i}:to`)!,
      lengthM: p.lengthM,
    };
    if (p.marker) {
      const fuseType = parseFuseMarker(p.marker);
      if (fuseType) cable.fuseTypeOverride = fuseType;
      else cable.label = p.marker;
    }
    if (p.seriesLink) cable.seriesLink = true;
    if (p.lengthEstimated) cable.lengthEstimated = true;
    return cable;
  });

  const cableIdsByConnection: string[][] = draft.connections.map(() => []);
  pending.forEach((p, i) => {
    cableIdsByConnection[p.connectionIndex].push(cables[i].id);
  });

  const zoneGroup = layoutGeneratedComponents(nodes, defByNodeId, cables);
  // Zones first: React Flow wants a parent ahead of its children.
  const zones = planGeneratedZones(nodes, defByNodeId, zoneGroup, usedIds);

  const candidate: System = {
    name: draft.name,
    ...(context.designProfileId !== undefined ? { designProfileId: context.designProfileId } : {}),
    nodes: [...zones, ...nodes],
    cables,
    zoneDistances: {},
    version: SCHEMA_VERSION,
  };

  const result = migrate(candidate);
  if (!result.ok) {
    return {
      ok: false,
      error: {
        kind: "invalid-system",
        message:
          result.error.kind === "not-system-shape"
            ? result.error.message
            : `File version ${result.error.fileVersion} is newer than ${result.error.supportedVersion}.`,
      },
    };
  }
  return {
    ok: true,
    system: { ...result.system, zoneDistances: impliedZoneDistances(result.system) },
    cableIdsByConnection,
  };
}

/** Lives here, not in the endpoint, so the eval harness re-asks with the endpoint's words. */
export function systemDraftRejection(error: SystemDraftError): string {
  switch (error.kind) {
    case "unknown-catalog-id":
      return `Rejected: ${error.catalogIds
        .map((id) => `"${id}"`)
        .join(", ")} ${error.catalogIds.length === 1 ? "is not a catalog id" : "are not catalog ids"}. Every catalogId must be copied exactly from the catalog list in the system prompt.`;
    case "ambiguous-engine-port":
      return ambiguousEnginePortRejection(error.ref);
    case "no-terminal":
      // Never suggest a bus bar fed from a fused output — that puts every branch
      // behind one fuse.
      return error.reason === "exhausted"
        ? `Rejected: component "${error.ref}" has only ${error.available} ${error.polarity} output${error.available === 1 ? "" : "s"}, and you wired more circuits than that onto it. Choose a part with enough outputs for every branch, or feed a fuse block from this one and take each branch off its own fused slot.`
        : `Rejected: component "${error.ref}" has no ${error.polarity} terminal, so it cannot take a ${error.polarity} cable. Wire it on the rail it actually has, or pick a part that has both.`;
    case "invalid-system":
      return `Rejected: the graph did not build (${error.message}).`;
  }
}

export type CollapsedCount = { ref: string; requested: number };

/**
 * Force `count` to 1 on every distribution part. `count` means *wired in parallel*,
 * and two parallel fused branches per load renders, fuses and passes every detector.
 */
export function collapseDistributionCounts(input: AiSystemDraft): {
  draft: AiSystemDraft;
  collapsed: CollapsedCount[];
} {
  const collapsed: CollapsedCount[] = [];
  const components = input.components.map((c) => {
    if (c.count <= 1) return c;
    if (getDefinition(c.catalogId)?.category !== "distribution") return c;
    collapsed.push({ ref: c.ref, requested: c.count });
    return { ...c, count: 1 };
  });
  if (!collapsed.length) return { draft: input, collapsed };
  return { draft: { ...input, components }, collapsed };
}

export type FuseBlockRelief =
  | { kind: "upsized"; ref: string; from: string; to: string; circuits: number }
  | { kind: "split"; ref: string; catalogId: string; units: number; circuits: number };

/** Splitting a block can over-subscribe whatever feeds it, so passes are bounded. */
const MAX_RELIEF_PASSES = 6;

/**
 * Make a fuse block big enough for the branches wired onto it: upsize within the
 * product line, else split onto a second unit. Never by hanging a bus bar off a
 * fused output — that would put several branches behind one fuse.
 */
export function relieveFuseBlocks(input: AiSystemDraft): {
  draft: AiSystemDraft;
  relieved: FuseBlockRelief[];
} {
  let draft = input;
  const relieved: FuseBlockRelief[] = [];
  for (let pass = 0; pass < MAX_RELIEF_PASSES; pass++) {
    const demands = slotDemands(draft);
    if (!demands.length) break;
    // The first shortfall that can be relieved: a block no move reaches must not
    // stall the ones that could be fixed.
    let applied: { draft: AiSystemDraft; relief: FuseBlockRelief } | null = null;
    for (const demand of demands) {
      applied = relieveSlotDemand(draft, demand);
      if (applied) break;
    }
    if (!applied) break;
    draft = applied.draft;
    relieved.push(applied.relief);
  }
  return { draft, relieved };
}

/** `claims` are the connections needing a slot, each with the cables it expands into. */
type SlotDemand = {
  ref: string;
  catalogId: string;
  polarity: AiSystemPolarity;
  slots: number;
  /** Always greater than `slots`. */
  wanted: number;
  claims: Array<{ index: number; cables: number }>;
  feeds: number[];
};

function slotDemands(draft: AiSystemDraft): SlotDemand[] {
  const defByRef = new Map<string, ComponentDefinition>();
  for (const c of draft.components) {
    const def = getDefinition(c.catalogId);
    // An unknown id is rejected by `draftToSystem` a step later, with the ids named.
    if (!def) return [];
    defByRef.set(c.ref, def);
  }
  const countByRef = new Map(draft.components.map((c) => [c.ref, c.count]));

  const demands: SlotDemand[] = [];
  for (const comp of draft.components) {
    const def = defByRef.get(comp.ref)!;
    if (!def.isFuseBlock) continue;
    if (fusedSlotCount(def) < 2) continue; // an inline fuse holder, not a block
    // The same call `draftToSystem` makes, so the two see one terminal list.
    const terminals = effectiveTerminals(def, {});

    const pools = new Map<
      string,
      { polarity: AiSystemPolarity; slots: number; claims: SlotDemand["claims"] }
    >();
    const feeds: number[] = [];
    draft.connections.forEach((conn, index) => {
      const isFrom = conn.from === comp.ref;
      if (!isFrom && conn.to !== comp.ref) return;
      const otherDef = defByRef.get(isFrom ? conn.to : conn.from);
      if (!otherDef) return;
      // `seriesLink` is only battery↔battery or panel↔panel, so no cable here
      // carries a per-end rail.
      const role = terminalRole(
        isFrom ? conn.fromPort : conn.toPort,
        isFrom,
        def,
        otherDef,
      );
      const pool = eligibleTerminals(def, terminals, conn.polarity, role);
      // Only an entirely exclusive pool is relieved: `pickTerminal` falls back to
      // any shareable terminal, so a pool holding the input post needs no bigger block.
      if (pool.length && pool.every((t) => isExclusiveTerminal(def, t))) {
        const cables = Math.max(
          countByRef.get(conn.from) ?? 1,
          countByRef.get(conn.to) ?? 1,
        );
        const key = pool.map((t) => t.id).join(",");
        const bucket = pools.get(key) ?? {
          polarity: conn.polarity,
          slots: pool.length,
          claims: [],
        };
        bucket.claims.push({ index, cables });
        pools.set(key, bucket);
        return;
      }
      // A sibling needs the block's feed or it has branches and no supply. Nothing
      // else is duplicated: a second negative off the integrated bus is a second return.
      if (role === "input") feeds.push(index);
    });

    for (const pool of pools.values()) {
      const wanted = pool.claims.reduce((n, c) => n + c.cables, 0);
      if (wanted <= pool.slots) continue;
      demands.push({
        ref: comp.ref,
        catalogId: comp.catalogId,
        polarity: pool.polarity,
        slots: pool.slots,
        wanted,
        claims: pool.claims,
        feeds,
      });
    }
  }
  return demands;
}

function relieveSlotDemand(
  draft: AiSystemDraft,
  demand: SlotDemand,
): { draft: AiSystemDraft; relief: FuseBlockRelief } | null {
  const def = getDefinition(demand.catalogId)!;
  const bigger = biggerFuseBlock(def, demand.polarity, demand.wanted);
  if (bigger) {
    return {
      draft: setDraftCatalogId(draft, demand.ref, bigger.id),
      relief: {
        kind: "upsized",
        ref: demand.ref,
        from: def.id,
        to: bigger.id,
        circuits: demand.wanted,
      },
    };
  }
  return splitFuseBlock(draft, demand, def);
}

function fusedSlotCount(def: ComponentDefinition): number {
  return def.terminals.filter((t) => t.direction === "output").length;
}

/**
 * Smallest part that can stand in for `def`. Same `fuseSlotType` keeps the fuse
 * family, and with it the interrupting rating the AIC walk reads.
 */
function biggerFuseBlock(
  def: ComponentDefinition,
  polarity: AiSystemPolarity,
  needed: number,
): ComponentDefinition | null {
  const slots = (d: ComponentDefinition) =>
    d.terminals.filter(
      (t) => t.direction === "output" && polarityMatches(d, t, polarity),
    ).length;
  const candidates = CATALOG.filter(
    (d) =>
      d.id !== def.id &&
      d.isFuseBlock === true &&
      d.category === def.category &&
      (d.fuseSlotType ?? null) === (def.fuseSlotType ?? null) &&
      (d.manufacturer ?? null) === (def.manufacturer ?? null) &&
      slots(d) >= needed &&
      coversTerminals(d, def),
  );
  // Then by id: catalog order is editorial and must not decide which part someone gets.
  candidates.sort((a, b) => slots(a) - slots(b) || (a.id < b.id ? -1 : 1));
  return candidates[0] ?? null;
}

/** At least as many terminals as `def` per rail+direction bucket, each rated as high. */
function coversTerminals(sub: ComponentDefinition, def: ComponentDefinition): boolean {
  const bucket = (t: Terminal) => `${t.polarity}:${t.direction}`;
  const groups = new Map<string, Terminal[]>();
  for (const t of def.terminals) push(groups, bucket(t), t);
  const ceiling = (ts: Terminal[]) => Math.min(...ts.map((t) => t.maxAmp ?? Infinity));
  for (const [key, wanted] of groups) {
    const got = sub.terminals.filter((t) => bucket(t) === key);
    if (got.length < wanted.length) return false;
    if (ceiling(got) < ceiling(wanted)) return false;
  }
  return true;
}

/**
 * Whole connections move, never part of one: a `count: 4` load is four branches the
 * draft cannot name individually, so a connection wider than a block is rejected.
 */
function splitFuseBlock(
  draft: AiSystemDraft,
  demand: SlotDemand,
  def: ComponentDefinition,
): { draft: AiSystemDraft; relief: FuseBlockRelief } | null {
  if (demand.claims.some((c) => c.cables > demand.slots)) return null;

  const free = [demand.slots];
  const unitOf = new Map<number, number>();
  for (const claim of demand.claims) {
    let unit = free.findIndex((remaining) => remaining >= claim.cables);
    if (unit === -1) {
      free.push(demand.slots);
      unit = free.length - 1;
    }
    free[unit] -= claim.cables;
    unitOf.set(claim.index, unit);
  }
  if (free.length < 2) return null;

  const original = draft.components.find((c) => c.ref === demand.ref)!;
  const used = new Set(draft.components.map((c) => c.ref));
  const siblings = free
    .slice(1)
    .map((_, i) => uniqueId(`${demand.ref}-${i + 2}`, used));

  const components = [...draft.components];
  components.splice(
    components.findIndex((c) => c.ref === demand.ref) + 1,
    0,
    ...siblings.map((ref, i) => ({
      ...original,
      ref,
      // Numbered from 2 — the first unit keeps the name the model gave it.
      label: original.label ? `${original.label} ${i + 2}` : null,
      count: 1,
    })),
  );

  const rehome = (conn: AiSystemDraft["connections"][number], ref: string) =>
    conn.from === demand.ref ? { ...conn, from: ref } : { ...conn, to: ref };

  const connections = draft.connections.map((conn, index) => {
    const unit = unitOf.get(index);
    return unit === undefined || unit === 0 ? conn : rehome(conn, siblings[unit - 1]);
  });
  // Appended, never spliced: a length chip carries the connection's index.
  for (const ref of siblings) {
    for (const index of demand.feeds) {
      connections.push(rehome(draft.connections[index], ref));
    }
  }

  return {
    draft: { ...draft, components, connections },
    relief: {
      kind: "split",
      ref: demand.ref,
      catalogId: def.id,
      units: free.length,
      circuits: demand.wanted,
    },
  };
}

/**
 * Labels are deliberately absent: engine code can't reach the i18n dictionary, so
 * the review UI renders them. `count-collapsed` and `block-*` are read-only.
 */
export type SystemDraftChip =
  | { kind: "part"; id: string; ref: string; catalogId: string; category: Category }
  | { kind: "count"; id: string; ref: string; value: number }
  | { kind: "count-collapsed"; id: string; ref: string; requested: number }
  /** `catalogId` is the block that was built, not the one the model asked for. */
  | {
      kind: "block-upsized";
      id: string;
      ref: string;
      catalogId: string;
      circuits: number;
    }
  | {
      kind: "block-split";
      id: string;
      ref: string;
      catalogId: string;
      units: number;
      circuits: number;
    }
  /** One physical run — both legs. Editing one leg alone leaves the pair inconsistent. */
  | {
      kind: "run";
      id: string;
      /** Every draft connection this row writes. One leg, or a +/− pair. */
      indexes: number[];
      /** The part both legs touch, or null on an unpaired leg. */
      sharedRef: string | null;
      fromRef: string;
      toRef: string;
      polarity: AiSystemPolarity;
      value: number;
    };

export function systemDraftChips(input: AiSystemDraft): SystemDraftChip[] {
  const chips: SystemDraftChip[] = [];
  // Chips are read off the draft *before* fuse-block relief: every editable chip
  // writes back into the draft the model returned, which has never heard of the
  // parts relief appends.
  const { draft, collapsed } = collapseDistributionCounts(input);
  const { relieved } = relieveFuseBlocks(draft);
  const requestedByRef = new Map(collapsed.map((c) => [c.ref, c.requested]));

  for (const c of draft.components) {
    const def = getDefinition(c.catalogId);
    if (c.partEstimated && def) {
      chips.push({
        kind: "part",
        id: `part:${c.ref}`,
        ref: c.ref,
        catalogId: c.catalogId,
        category: def.category,
      });
    }
    const requested = requestedByRef.get(c.ref);
    if (requested !== undefined) {
      // Regardless of `countEstimated`: our correction to report, not a guess to confirm.
      chips.push({
        kind: "count-collapsed",
        id: `collapsed:${c.ref}`,
        ref: c.ref,
        requested,
      });
    } else if (c.countEstimated && c.count > 1) {
      chips.push({ kind: "count", id: `count:${c.ref}`, ref: c.ref, value: c.count });
    }
  }

  for (const relief of relieved) {
    chips.push(
      relief.kind === "upsized"
        ? {
            kind: "block-upsized",
            id: `block:${relief.ref}`,
            ref: relief.ref,
            catalogId: relief.to,
            circuits: relief.circuits,
          }
        : {
            kind: "block-split",
            id: `block:${relief.ref}`,
            ref: relief.ref,
            catalogId: relief.catalogId,
            units: relief.units,
            circuits: relief.circuits,
          },
    );
  }

  for (const group of groupRuns(draft)) {
    const first = draft.connections[group.indexes[0]];
    chips.push({
      kind: "run",
      id: `run:${group.indexes.join("+")}`,
      indexes: group.indexes,
      sharedRef: group.sharedRef,
      fromRef: first.from,
      toRef: first.to,
      polarity: first.polarity,
      value: first.lengthM,
    });
  }

  return chips;
}

/**
 * Pair each estimated + leg with its return. Capped at a pair: three cables at one
 * length through one part are three runs that happen to agree. Keys come from the
 * indexes — a chip id that moved between renders would drop its confirmed state.
 */
function groupRuns(
  draft: AiSystemDraft,
): Array<{ indexes: number[]; sharedRef: string | null }> {
  const estimated = draft.connections
    .map((conn, index) => ({ conn, index }))
    .filter(({ conn }) => conn.lengthEstimated);

  const taken = new Set<number>();
  const groups: Array<{ indexes: number[]; sharedRef: string | null }> = [];

  for (const { conn, index } of estimated) {
    if (taken.has(index)) continue;
    taken.add(index);
    const partner = estimated.find(
      ({ conn: other, index: otherIndex }) =>
        !taken.has(otherIndex) &&
        other.polarity !== conn.polarity &&
        other.lengthM === conn.lengthM &&
        sharedRef(conn, other) !== null,
    );
    if (!partner) {
      groups.push({ indexes: [index], sharedRef: null });
      continue;
    }
    taken.add(partner.index);
    const legs =
      conn.polarity === "positive" ? [index, partner.index] : [partner.index, index];
    groups.push({ indexes: legs, sharedRef: sharedRef(conn, partner.conn) });
  }
  return groups;
}

function sharedRef(
  a: AiSystemConnectionDraft,
  b: AiSystemConnectionDraft,
): string | null {
  for (const ref of [a.from, a.to]) {
    if (b.from === ref || b.to === ref) return ref;
  }
  return null;
}

export function setDraftCatalogId(
  draft: AiSystemDraft,
  ref: string,
  catalogId: string,
): AiSystemDraft {
  return {
    ...draft,
    components: draft.components.map((c) => (c.ref === ref ? { ...c, catalogId } : c)),
  };
}

export function setDraftCount(
  draft: AiSystemDraft,
  ref: string,
  count: number,
): AiSystemDraft {
  const clamped = Math.min(8, Math.max(1, Math.round(count)));
  return {
    ...draft,
    components: draft.components.map((c) =>
      c.ref === ref ? { ...c, count: clamped } : c,
    ),
  };
}

/** Move runs by a delta, keeping the spread between them — never onto one number. */
export function shiftDraftLengths(
  draft: AiSystemDraft,
  indexes: readonly number[],
  deltaM: number,
): AiSystemDraft {
  if (!deltaM) return draft;
  const targets = new Set(indexes);
  return {
    ...draft,
    connections: draft.connections.map((c, i) =>
      targets.has(i)
        ? {
            ...c,
            lengthM: Math.min(
              30,
              Math.max(0.1, Math.round((c.lengthM + deltaM) * 10) / 10),
            ),
          }
        : c,
    ),
  };
}

/** Set the length of one run — every leg of it. */
export function setDraftLength(
  draft: AiSystemDraft,
  indexes: readonly number[],
  lengthM: number,
): AiSystemDraft {
  const clamped = Math.min(30, Math.max(0.1, Math.round(lengthM * 10) / 10));
  const targets = new Set(indexes);
  return {
    ...draft,
    connections: draft.connections.map((c, i) =>
      targets.has(i) ? { ...c, lengthM: clamped } : c,
    ),
  };
}

/** Exported so `system-edit.ts` builds added parts the same way. */
export function buildDraftNode(
  id: string,
  def: ComponentDefinition,
  draft: AiSystemComponentDraft,
  index: number,
): ComponentNode {
  const node: ComponentNode = {
    id,
    kind: "component",
    definitionId: def.id,
    position: { x: 0, y: 0 },
    ...(def.defaultFuseAmp != null && { fuseAmp: def.defaultFuseAmp }),
  };
  if (draft.label) {
    node.label = draft.count > 1 ? `${draft.label} ${index + 1}` : draft.label;
  }
  if (draft.partEstimated) node.partEstimated = true;
  return node;
}

/**
 * The flag tells `detectReversedPolarity` a +→− bond is deliberate, so one on the
 * wrong pair silences the dead-short detector on a real dead short. Same rule
 * `detectSuspiciousSeriesLink` enforces: both batteries, or both solar panels.
 */
export function seriesLinkHonoured(
  requested: boolean,
  from: ComponentDefinition | undefined,
  to: ComponentDefinition | undefined,
): boolean {
  if (!requested || !from || !to) return false;
  if (from.category === "battery" && to.category === "battery") return true;
  return from.category === "solar" && to.category === "solar";
}

/**
 * Which side of a two-sided part a cable end lands on. The wrong side is a *silent*
 * mis-wire: the graph is valid and every detector stays quiet.
 */
export type TerminalRole = "input" | "output";

const CONSUMER_CATEGORIES: readonly Category[] = ["engine", "load"];

/** A starter feed and an alternator output are separate positive circuits.
 * Neither cable direction nor a battery at the other end identifies which one
 * was intended. Shared by initial drafts and edits before terminal selection. */
export function ambiguousEnginePort(def: ComponentDefinition, terminals: Terminal[], polarity: AiSystemPolarity, port: AiSystemPort): boolean {
  return def.category === "engine" && polarity === "positive" && port === "any" &&
    terminals.some(terminal => terminal.polarity === "positive" && terminal.direction === "input") &&
    terminals.some(terminal => terminal.polarity === "positive" && terminal.direction === "output");
}

export function ambiguousEnginePortRejection(ref: string): string {
  return `Rejected: engine "${ref}" has separate starter input and alternator output terminals. Set its positive connection port explicitly to "input" for the starter or "output" for the alternator; "any" is ambiguous. A starter feed does not connect the alternator output. Keep those as separate connections when both are requested.`;
}

/**
 * Which side of `self` a cable end lands on, or null when the part has no side to get
 * wrong. Deliberately no mirror rule for producers: a DC-DC charger's input comes from
 * a battery too, so "a battery means output" would put the starter on an Orion's out+.
 */
export function terminalRole(
  port: AiSystemPort,
  isFrom: boolean,
  self: ComponentDefinition,
  other: ComponentDefinition,
): TerminalRole | null {
  if (port === "input" || port === "output") return port;
  if (other.category === "solar" && self.category !== "solar") return "input";
  if (other.category === "battery" && CONSUMER_CATEGORIES.includes(self.category)) {
    return "input";
  }
  if (other.renderVariant === "chassis-ground") return null;
  return isFrom ? "output" : "input";
}

/** A slot *is* a fuse: two branches on one are two circuits behind one rating. */
function isExclusiveTerminal(def: ComponentDefinition, t: Terminal): boolean {
  return def.isFuseBlock === true && t.direction === "output";
}

/** In the order they are tried. Shared with `slotDemands`, which counts the same pool. */
function eligibleTerminals(
  def: ComponentDefinition,
  terminals: Terminal[],
  polarity: AiSystemPolarity,
  role: TerminalRole | null,
): Terminal[] {
  const candidates = terminals.filter((t) => polarityMatches(def, t, polarity));
  if (!candidates.length) return [];
  const matching = role ? candidates.filter((t) => t.direction === role) : [];
  const bidi = role ? candidates.filter((t) => t.direction === "bidi") : [];
  // Catalog order within each group: a busbar's input post sorts ahead of its studs.
  const ordered =
    role === null || (!matching.length && !bidi.length) ? candidates : [...matching, ...bidi];
  // A return bar is spun 180°, so a cable on its input post wraps around the bar.
  // Sorting it last costs nothing: every terminal on the bar is the same negative post.
  if (!isReturnBar(def)) return ordered;
  return [
    ...ordered.filter((t) => isStudTerminal(def, t)),
    ...ordered.filter((t) => !isStudTerminal(def, t)),
  ];
}

export type TerminalPick =
  | { ok: true; terminal: Terminal }
  | { ok: false; reason: "missing" | "exhausted"; available: number };

/**
 * Role is a *filter*, not a preference: a free terminal on the wrong side of the part
 * must never beat a taken one on the right side. A part with no terminal on the
 * asked-for side has no side to get wrong, so the filter opens back up rather than
 * failing. `stacked` counts landings per terminal id, not membership.
 */
export function pickTerminal(
  def: ComponentDefinition,
  terminals: Terminal[],
  polarity: AiSystemPolarity,
  role: TerminalRole | null,
  stacked: Map<string, number>,
): TerminalPick {
  const eligible = eligibleTerminals(def, terminals, polarity, role);
  if (!eligible.length) return { ok: false, reason: "missing", available: 0 };

  const free = eligible.filter((t) => !stacked.has(t.id));
  if (free.length) return { ok: true, terminal: free[0] };

  const capped = stackLimited(def);
  const shareable = eligible.filter(
    (t) =>
      !isExclusiveTerminal(def, t) &&
      (!capped || (stacked.get(t.id) ?? 0) < MAX_TERMINALS_PER_STUD),
  );
  if (!shareable.length) {
    return { ok: false, reason: "exhausted", available: eligible.length };
  }
  return { ok: true, terminal: shareable[0] };
}

/**
 * `"neutral"` does double duty: a polarity-agnostic DC post, and AC line/neutral that
 * no DC cable may touch. Told apart structurally — a part that declares its own +/−
 * rails has said where DC goes, so its `neutral` pins are the AC ones.
 */
function polarityMatches(
  def: ComponentDefinition,
  t: Terminal,
  polarity: AiSystemPolarity,
): boolean {
  if (t.polarity === "neutral") return !hasDcRailTerminals(def);
  if (polarity === "positive") return t.polarity === "positive";
  return t.polarity === "negative" || t.polarity === "ground";
}

function hasDcRailTerminals(def: ComponentDefinition): boolean {
  return def.terminals.some(
    (t) => t.polarity === "positive" || t.polarity === "negative",
  );
}

/**
 * Arrange the system in current-flow order. Returns which area each part ended up in —
 * the input `planGeneratedZones` needs, and which only the bands can answer.
 */
export function layoutGeneratedComponents(
  nodes: ComponentNode[],
  defByNodeId: Map<string, ComponentDefinition>,
  cables: Cable[],
  { reserveZoneSpace = true }: { reserveZoneSpace?: boolean } = {},
): Map<string, GeneratedZoneKind> {
  const neighbours = new Map<string, string[]>();
  for (const c of cables) {
    push(neighbours, c.fromNodeId, c.toNodeId);
    push(neighbours, c.toNodeId, c.fromNodeId);
  }
  const touches = (id: string, category: Category): boolean =>
    (neighbours.get(id) ?? []).some((n) => defByNodeId.get(n)?.category === category);

  // At 180° the bounding box is unchanged, so spinning here doesn't disturb placement.
  for (const node of nodes) {
    const def = defByNodeId.get(node.id)!;
    if (isReturnBar(def)) node.rotation = 180;
  }

  const sizeOf = (node: ComponentNode) =>
    effectiveDimensions(defByNodeId.get(node.id)!, node);

  const solar: ComponentNode[] = [];
  const columns = new Map<number, ComponentNode[]>();
  const columnByNode = new Map<string, number>();
  for (const node of nodes) {
    const def = defByNodeId.get(node.id)!;
    if (def.category === "solar") {
      solar.push(node);
      continue;
    }
    const column = columnOfNode(def, touches(node.id, "battery"));
    columnByNode.set(node.id, column);
    push(columns, column, node);
  }

  const wired = new Set<string>();
  for (const c of cables) {
    wired.add(`${c.fromNodeId}:${c.fromTerminalId}`);
    wired.add(`${c.toNodeId}:${c.toTerminalId}`);
  }
  aimLoadReturns(columns.get(COL_LOAD) ?? [], defByNodeId, (nodeId, terminalId) =>
    wired.has(`${nodeId}:${terminalId}`),
  );

  const columnX = new Map<number, number>();
  let x = 0;
  for (let column = COL_SOURCE; column <= COL_RETURN; column++) {
    columnX.set(column, x);
    const members = columns.get(column);
    if (!members) continue;
    // Stable sort: same-rank parts keep the model's order, so two batteries stay 1, 2.
    members.sort((a, b) => rowRank(a, defByNodeId, touches) - rowRank(b, defByNodeId, touches));
    const width = Math.max(...members.map((n) => sizeOf(n).width));
    const height =
      members.reduce((sum, n) => sum + sizeOf(n).height, 0) +
      ROW_GAP_PX * (members.length - 1);
    let y = -height / 2;
    for (const node of members) {
      const size = sizeOf(node);
      node.position = { x: Math.round(x + (width - size.width) / 2), y: Math.round(y) };
      y += size.height + ROW_GAP_PX;
    }
    x += width + COLUMN_GAP_PX[column];
  }

  // The tap band already has its x from the band loop; this is its battery-aligned y.
  const taps = columns.get(COL_TAP);
  if (taps) {
    const centreOfBatteries = (node: ComponentNode): number | null => {
      const posts = (neighbours.get(node.id) ?? []).filter(
        (n) => defByNodeId.get(n)?.category === "battery",
      );
      if (!posts.length) return null;
      let sum = 0;
      for (const id of posts) {
        const post = nodes.find((n) => n.id === id)!;
        sum += post.position.y + sizeOf(post).height / 2;
      }
      return sum / posts.length;
    };
    const targets = new Map<string, number>();
    for (const node of taps) {
      const centre = centreOfBatteries(node);
      if (centre !== null) targets.set(node.id, centre);
    }
    const aligned = taps.filter((n) => targets.has(n.id));
    aligned.sort((a, b) => targets.get(a.id)! - targets.get(b.id)!);
    let floor = -Infinity;
    for (const node of aligned) {
      const size = sizeOf(node);
      const y = Math.max(targets.get(node.id)! - size.height / 2, floor);
      node.position = { ...node.position, y: Math.round(y) };
      floor = y + size.height + ROW_GAP_PX;
    }
  }

  if (solar.length) {
    const feeds = solar
      .flatMap((n) => neighbours.get(n.id) ?? [])
      .filter((id) => defByNodeId.get(id)?.category !== "solar")
      .map((id) => nodes.find((n) => n.id === id)!.position.x);
    const anchorX = feeds.length ? Math.min(...feeds) : (columnX.get(COL_CONVERT) ?? 0);
    // Zoned drawings move loads below the panel later. Unzoned drawings keep
    // them beside it, so a wide array must clear every column it reaches.
    const arrayRight = anchorX + solar.reduce((sum, n) => sum + sizeOf(n).width, 0) +
      ROW_GAP_PX * (solar.length - 1);
    const under = nodes.filter((n) => reserveZoneSpace
      ? (columnByNode.get(n.id) ?? COL_RETURN) <= COL_BRANCH
      : columnByNode.has(n.id) && n.position.x < arrayRight + ROW_GAP_PX &&
        n.position.x + sizeOf(n).width > anchorX - ROW_GAP_PX);
    const highest = under.length ? Math.min(...under.map((n) => n.position.y)) : 0;
    const bandHeight = Math.max(...solar.map((n) => sizeOf(n).height));
    const bandY = highest - SOLAR_BAND_GAP_PX - bandHeight;
    let panelX = anchorX;
    for (const node of solar) {
      const size = sizeOf(node);
      node.position = {
        x: Math.round(panelX),
        y: Math.round(bandY + (bandHeight - size.height) / 2),
      };
      panelX += size.width + ROW_GAP_PX;
    }
  }

  const zoneGroup = new Map<string, GeneratedZoneKind>();
  for (const node of solar) zoneGroup.set(node.id, "roof");
  for (const [column, members] of columns) {
    const kind = ZONE_GROUP_OF_COLUMN[column];
    for (const node of members) zoneGroup.set(node.id, kind);
  }

  // Only the panel group is in the way: the bank is left of the loads, the roof above.
  const loads = columns.get(COL_LOAD) ?? [];
  if (reserveZoneSpace && loads.length) {
    const panelNodes = nodes.filter((n) => zoneGroup.get(n.id) === "panel");
    if (panelNodes.length) {
      const panelBottom = Math.max(
        ...panelNodes.map((n) => n.position.y + sizeOf(n).height),
      );
      const loadTop = Math.min(...loads.map((n) => n.position.y));
      const drop = panelBottom + LOAD_BAND_GAP_PX - loadTop;
      if (drop > 0) {
        for (const node of loads) {
          node.position = { ...node.position, y: Math.round(node.position.y + drop) };
        }
      }
    }
  }

  return zoneGroup;
}

/** The return bar files with the panel, not the loads it sits beside — see `system-zones.ts`. */
const ZONE_GROUP_OF_COLUMN: Record<number, GeneratedZoneKind> = {
  [COL_SOURCE]: "bank",
  [COL_TAP]: "bank",
  [COL_CONVERT]: "panel",
  [COL_POS_BUS]: "panel",
  [COL_BRANCH]: "panel",
  [COL_LOAD]: "loads",
  [COL_RETURN]: "panel",
};

/** Best first: the return bar is right of every load; bottom and top are fallbacks. */
const RETURN_FACING_SIDES: TerminalSide[] = ["right", "bottom", "top"];

/**
 * Turn each load's negative onto the face pointing at the common return. Asserted from
 * the bands, not per cable. Only loads: elsewhere the catalog has the real faces.
 */
function aimLoadReturns(
  loads: ComponentNode[],
  defByNodeId: Map<string, ComponentDefinition>,
  isConnected: (nodeId: string, terminalId: string) => boolean,
): void {
  for (const node of loads) {
    const def = defByNodeId.get(node.id)!;
    const negatives = def.terminals.filter(
      (t) =>
        (t.polarity === "negative" || t.polarity === "ground") &&
        isConnected(node.id, t.id),
    );
    if (!negatives.length) continue;
    const moving = new Set(negatives.map((t) => t.id));
    const side = RETURN_FACING_SIDES.find(
      (candidate) => !def.terminals.some((t) => t.side === candidate && !moving.has(t.id)),
    );
    if (!side) continue;
    const overrides = { ...node.terminalOverrides };
    negatives.forEach((t, i) => {
      overrides[t.id] = { side, offset: (i + 1) / (negatives.length + 1) };
    });
    node.terminalOverrides = overrides;
  }
}

function columnOfNode(def: ComponentDefinition, batteryAdjacent: boolean): number {
  switch (def.category) {
    case "battery":
    case "engine":
    case "source":
      return COL_SOURCE;
    case "charger":
    case "solar":
      return COL_CONVERT;
    case "monitor":
      // A shunt is a battery accessory in the bank's negative, an instrument otherwise.
      return batteryAdjacent ? COL_TAP : COL_CONVERT;
    case "load":
      return COL_LOAD;
    case "distribution":
      if (isNegativeReturn(def)) return COL_RETURN;
      if (def.renderVariant === "busbar") return COL_POS_BUS;
      if (def.isFuseBlock === true && def.renderVariant !== "fuse-holder") return COL_BRANCH;
      return batteryAdjacent ? COL_TAP : COL_CONVERT;
  }
}

/** Read off the terminals, not the catalog id, so a bar added later lands in the band. */
function isNegativeReturn(def: ComponentDefinition): boolean {
  if (def.renderVariant === "chassis-ground") return true;
  if (def.renderVariant !== "busbar") return false;
  return def.terminals.every((t) => t.polarity === "negative");
}

/** The bars `layoutGeneratedComponents` spins 180°. Stud count and terminal order both depend on the spin. */
export function isReturnBar(def: ComponentDefinition): boolean {
  return def.renderVariant === "busbar" && isNegativeReturn(def);
}

function rowRank(
  node: ComponentNode,
  defByNodeId: Map<string, ComponentDefinition>,
  touches: (id: string, category: Category) => boolean,
): number {
  const def = defByNodeId.get(node.id)!;
  if (def.category === "battery" && touches(node.id, "engine")) return ROW_START_BATTERY;
  return ROW_ORDER[def.category];
}

function otherEnd(
  p: { fromNodeId: string; toNodeId: string },
  nodeId: string,
): string {
  return p.fromNodeId === nodeId ? p.toNodeId : p.fromNodeId;
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

function slug(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function uniqueId(base: string, used: Set<string>): string {
  let id = base;
  let n = 2;
  while (used.has(id)) id = `${base}-${n++}`;
  used.add(id);
  return id;
}
